// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.

import {Injectable} from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {ConfigurationService} from './configuration-service';
import {
  IApproversResponse,
  IApproversStatusResponse, ISecurityRequest,
  ISecurityRequestPost,
  ISecurityRequestResponse,
  ISecurityRequestToApprove,
  ISecurityResultResponse,
  PostSecurityResponseStatus,
  SecurityRequestResult,
  SecurityRequestStatus,
  SecurityRequestType
} from '../types/security-requests';
import {BehaviorSubject, EMPTY, Observable, Subject} from 'rxjs';
import {API_URL} from '../shared/api';
import {catchError, map, tap} from 'rxjs/operators';
import {NotificationService} from './notification-service';
import {TranslateService} from '@ngx-translate/core';

export interface IRequestsResponse {
  success: boolean;
  requests: ISecurityRequest[];
}


@Injectable({
  providedIn: 'root'
})
export class SecurityRequestService {

  readonly refresh$: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
  readonly found$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly updated$: Subject<void> = new Subject<void>();

  constructor(
      private readonly http: HttpClient,
      private readonly configurationService: ConfigurationService,
      private readonly notificationService: NotificationService,
      private readonly translate: TranslateService,
  ) { }

  getUrl(suffix) {
    return `${this.configurationService.apiUrl}${suffix}`;
  }

  postSecurityRequest(
      targetUserId: number,
      requestType: SecurityRequestType,
      target_disk_serial: string = null,
      otherData: { [key: string]: string } = null,
      notifyApprovers = false,
  ): Observable<void> {
    const url = this.getUrl(API_URL.SECURITY_REQUEST);
    const data: ISecurityRequestPost = {
      type: requestType,
      target_id: targetUserId,
      target_disk_serial: target_disk_serial,
      data: otherData,
      email_approvers: notifyApprovers
    };
    return this.http.post<void>(url, data).pipe(
      catchError((err: HttpErrorResponse, caught) => {
        const status: PostSecurityResponseStatus = err.error.status;
        let message: string = null;
        switch (status) {
          case PostSecurityResponseStatus.MISSING_TYPE_OR_TARGET:
            message = this.translate.instant("post-security-response-status.missing-type-or-target");
            break;
          case PostSecurityResponseStatus.INVALID_REQUEST_TYPE:
            message = this.translate.instant("post-security-response-status.invalid-request-type");
            break;
          case PostSecurityResponseStatus.TARGET_USER_INVALID_OR_INACTIVE:
            message = this.translate.instant("post-security-response-status.target-user-invalid-or-inactive");
            break;
          case PostSecurityResponseStatus.USER_TARGET_USER_GROUP_MISMATCH:
            message = this.translate.instant("post-security-response-status.user-target-user-group-mismatch");
            break;
          case PostSecurityResponseStatus.RECOVERY_KEY_DISABLED:
            message = this.translate.instant("post-security-response-status.recovery-key-disabled");
            break;
          case PostSecurityResponseStatus.DISK_CONFIG_NON_CUSTOMER:
            message = this.translate.instant("post-security-response-status.disk-config-non-customer");
            break;
          case PostSecurityResponseStatus.BITLOCKER_KEY_NON_CUSTOMER:
            message = this.translate.instant("post-security-response-status.bitlocker-key-non-customer");
            break;
          case PostSecurityResponseStatus.ADD_APPROVER_INVALID_APPROVER_STATUS:
            message = this.translate.instant("post-security-response-status.add-approver-invalid-approver-status");
            break;
          case PostSecurityResponseStatus.REMOVE_APPROVER_INVALID_APPROVER_STATUS:
            message = this.translate.instant("post-security-response-status.remove-approver-invalid-approver-status");
            break;
          case PostSecurityResponseStatus.ATTEMPTED_REMOVAL_OF_LAST_APPROVER:
            message = this.translate.instant("post-security-response-status.attempted-removal-of-last-approver");
            break;
          case PostSecurityResponseStatus.EXISTING_PENDING_OR_APPROVED_REQUEST:
            message = this.translate.instant("post-security-response-status.existing-pending-or-approved-request");
            break;
          case PostSecurityResponseStatus.EXISTING_ACCEPTED_REQUEST:
            message = this.translate.instant("post-security-response-status.existing-accepted-request");
            break;
        }
        if (!message) {
          message = this.translate.instant("ERROR_OCCURRED_UNKNOWN");
        }
        this.notificationService.showError(message);
        return EMPTY;
      }),
      tap(() => {
        this.updated$.next(null);
      }),
    );
  }

  getRequestToApprove(): Observable<ISecurityRequestToApprove> {
    const url = this.getUrl(API_URL.SECURITY_REQUEST_TO_APPROVE);
    return this.http.get<ISecurityRequestToApprove>(url);
  }

  getCurrentRequest(disk_serial: string = null, data: string = null, request_type: string = null): Observable<ISecurityRequestResponse> {
    let url = this.getUrl(API_URL.SECURITY_CURRENT_REQUEST);

    if ( disk_serial != null ) {
      url = url.concat('?disk_serial=', disk_serial);
    }
    if ( data != null ) {
      url = url.concat('?data=', data);
      url = url.concat('&request_type=', request_type)
    }

    return this.http.get<ISecurityRequestResponse>(url);
  }

  getRequestsByTargetUser(userId: number): Observable<ISecurityRequest[]> {
    const url = this.getUrl(`/api/v1/security-requests/${userId}`);
    return this.http.get<IRequestsResponse>(url).pipe(
      map(response => response.requests)
    );
  }

  getRequestById(requestId: number): Observable<ISecurityRequestResponse> {
    const url = this.getUrl(`/api/v1/security-request/${requestId}`);
    return this.http.get<ISecurityRequestResponse>(url);
  }

  postSecurityStatus(requestId: number, result: SecurityRequestResult): Observable<ISecurityResultResponse> {
    const url = this.getUrl(`${API_URL.SECURITY_REQUEST}/${requestId}`);
    return this.http.post<ISecurityResultResponse>(url, {result}).pipe(
        tap(() => {
          this.updated$.next(null);
        })
    );
  }

  postSecurityStatusByToken(token: string, result: SecurityRequestResult): Observable<ISecurityResultResponse> {
    const url = this.getUrl(API_URL.SECURITY_REQUEST_BY_TOKEN);
    return this.http.post<ISecurityResultResponse>(url, {result}, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  getApprovers(): Observable<IApproversResponse> {
    const url = this.getUrl(API_URL.SECURITY_REQUEST_APPROVERS);
    return this.http.get<IApproversResponse>(url);
  }

  getApproversByUser(userId: number): Observable<IApproversResponse> {
    const url = this.getUrl(`${API_URL.SECURITY_REQUEST_APPROVERS}/${userId}`);
    return this.http.get<IApproversResponse>(url);
  }

  getApproversStatus(requestId: number): Observable<IApproversStatusResponse> {
    const url = this.getUrl(`${API_URL.SECURITY_REQUEST_APPROVERS_STATUS}/${requestId}`);
    return this.http.get<IApproversStatusResponse>(url);
  }

  putCancelRequest(requestId: number): Observable<void> {
    const url = this.getUrl(`${API_URL.SECURITY_REQUEST}/${requestId}`);
    return this.http.put<void>(url, {status: SecurityRequestStatus.CANCELLED}).pipe(
        tap(() => {
          this.updated$.next(null);
        })
    );
  }

  putAcceptRequest(requestId: number): Observable<void> {
    const url = this.getUrl(`${API_URL.SECURITY_REQUEST}/${requestId}`);
    return this.http.put<void>(url, {status: SecurityRequestStatus.ACCEPTED}).pipe(
        tap(() => {
          this.updated$.next(null);
        })
    );
  }

  getRequests(): Observable<ISecurityRequest[]> {
    const url = this.getUrl(`/api/v1/security-requests`);
    return this.http.get<IRequestsResponse>(url).pipe(
      map(response => response.requests)
    );
  }
}
